import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, GenesisUnitOfWork, ReportService, SessionInformationService, UtilityService } from 'app/core/core.module';
import { InvoiceService } from 'app/core/data/invoice-service';
import { Dealer } from 'app/core/model/dealer';
import { Invoice, Setting } from 'app/core/model/entity-model';
import { PackingSlip } from 'app/core/model/packing-slip';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment';

@Component({
    selector: 'app-timing-overview',
    templateUrl: './timing-overview.component.html',
    styleUrls: ['./timing-overview.component.scss'],
    standalone: false,
})
export class TimingOverviewComponent implements OnInit {
    reports: any[] = ['OnTime Build', 'OnTime Delivery'];
    sessionStorageKey = 'timing-filter';
    filter: any;
    entities: any[];
    chartEntities: any[];
    summaryChart: any[];
    dealers: Dealer[];
    average: number = 0;
    onTimePercentage: number = 0;
    onTimeTargetPercent: number;

    build: boolean = false;
    delivery: boolean = false;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: GenesisUnitOfWork,
        private utility: UtilityService,

        private router: Router,
        private reportService: ReportService,
        private sessionInfo: SessionInformationService,
        private appInfo: AppInformationService,
        private invoiceService: InvoiceService,
    ) {}

    async ngOnInit() {
        this.dealers = await this.uow.getDealers();
        this.onTimeTargetPercent = ((await this.uow.getSettingsByName('OnTimeTargetPercent')) as Setting).decimalValue;
        const filter = this.sessionInfo.getItem(this.sessionStorageKey);
        if (filter) {
            this.filter = filter;
            await this.search();
        } else this.filter = {};
    }

    async search() {
        this.appInfo.pleaseWaitShow();
        let result: any;
         this.build = this.filter.report === 'OnTime Build';
         this.delivery = this.filter.report === 'OnTime Delivery';
        if (this.filter.report === 'OnTime Build') {
            result = await this.utility.onTimeBuildOverview(this.filter.startDate, this.filter.endDate, this.filter.basedOnDate);
        } else {
            result = await this.utility.onTimeDeliveryOverview(this.filter.startDate, this.filter.endDate, this.filter.basedOnDate);
        }
        this.entities = result.details;
        const groupedByBuildDueWeek = _.groupBy(this.entities, 'weekDue');
        this.chartEntities = Object.keys(groupedByBuildDueWeek).map((key) => ({
            weekDue: moment(key).format('MM/DD/YYYY'),
            onTimeCount: groupedByBuildDueWeek[key].filter((x) => (this.build ? x.built : x.delivered) && x.daysOnTime >= 0).length,
            lateCount: groupedByBuildDueWeek[key].filter((x) => (this.build ? x.built : x.delivered) && x.daysOnTime < 0).length,
        }));
        this.average = result.average;
        this.onTimePercentage = result.onTimePercentage;

        const totalCount = this.entities.length;
        const onTimeCount = this.entities.filter((x) => (this.build ? x.built : x.delivered) && x.daysOnTime >= 0).length;
        const lateCount = this.entities.filter((x) => (this.build ? x.built : x.delivered) && x.daysOnTime < 0).length;
        const unKnownCount = totalCount-onTimeCount-lateCount;

        this.summaryChart = [{ series: 'Overall', onTime: this.onTimePercentage, late: 1 - this.onTimePercentage, 
            onTimeTrailers: onTimeCount, lateTrailers: lateCount, totalTrailers: totalCount, unKnownCount: unKnownCount }];
        this.sessionInfo.setItem(this.sessionStorageKey, this.filter);
        this.appInfo.pleaseWaitHide();
    }

    clear() {
        this.filter = {};
    }

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.dataGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.dataGrid.instance.clearSelection();
        }
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            }
        );
    }

    async onRowDoubleClick(e) {
        this.open(e.data);
    }

    open(invoice: any) {
        if (!invoice) return;
        this.router.navigate([`/invoices/${invoice.id}`]);
    }

    async openInvoiceOrder(invoiceNumber: number) {
        const order = (await this.uow.getOrdersByInvoiceNumbers([invoiceNumber]))[0];
        if (!order) return;
        this.router.navigate([`/sales-orders/${order.id}`]);
    }

    async printPackingSlip(packSlipNumber: any) {
        const reportGuid = ((await this.uow.getPackSlipByNumber(packSlipNumber)) as PackingSlip).reportGuid;
        this.reportService.openPackingSlip([reportGuid]);
    }

    async printInvoice(cell: any) {
        if (cell.column.dataField === 'invoiceNumber') {
            const reportGuid = ((await this.uow.getInvoiceByInvoiceNumber(cell.value)) as Invoice).reportGuid;
            this.reportService.openInvoiceReport([reportGuid]);
        }
    }

    async applyPayment() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        await this.invoiceService.receivePayment(selectedRows[0]);
    }

    async queueForQBBridge() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        const result = await confirm(
            `Are you sure you want to ReQueue the selected ${selectedRows.length === 1 ? 'invoice' : 'invoices'} for the Bridge?`,
            `${this.appInfo.settings.company}`
        );
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        const ids = selectedRows.map((x) => {
            return x.id;
        });
        const invoices = (await this.uow.invoices.byIds(ids)) as Invoice[];
        invoices.forEach((row) => {
            row.quickBooksLastExport = null;
        });
        await this.uow.commit();
        await this.search();
        this.appInfo.pleaseWaitHide();
    }
}
